import { Document, DocumentError, type DocumentJSON, type Op, type Presence, type ServerMessage, type Transaction, type NodeId, type Comment, type CommentAction, type VoteAction, type VotingSession } from '@vellum/core';

export interface SyncHandlers {
  onInit(doc: DocumentJSON, clientId: string, fileId: string, peers: Presence[], role: 'owner' | 'editor' | 'viewer'): void;
  /** The server refused this file (no access); no reconnects follow. */
  onDenied?(reason: string): void;
  onRemote(tx: Transaction): void;
  onPresence(p: Presence): void;
  onLeave(clientId: string): void;
  onFocus(msg: Extract<ServerMessage, { type: 'focus' }>): void;
  onStatus(status: 'connecting' | 'online' | 'offline'): void;
  onComments(comments: Comment[]): void;
  onVoting?(session: VotingSession | null): void;
  onNotice(message: string, from: string): void;
  onRtc?(from: string, data: unknown): void;
  onIce?(servers: RTCIceServer[]): void;
}

/**
 * Client side of the multiplayer protocol.
 * Server order is authoritative (last writer wins per property). While a local change to
 * (node, prop) is unacknowledged, remote writes to the same (node, prop) are ignored —
 * the server will apply ours after theirs, so ignoring theirs converges to the same state.
 */
export interface SyncOptions {
  /** Base ws(s) URL, e.g. ws://localhost:7878 (default: the page's host). */
  url?: string;
  /** WebSocket implementation (default: globalThis.WebSocket). */
  WebSocket?: typeof WebSocket;
  clientId?: string;
  /**
   * Connect (and download the file) right away, but hand messages over only once this settles:
   * the app connects while its fonts still load, and lays the file out with the real ones.
   */
  ready?: Promise<unknown>;
}

export class SyncClient {
  private ws: WebSocket | null = null;
  private seq = 0;
  private pending = new Map<number, Transaction>();
  private pendingProps = new Map<NodeId, Map<string, number>>();
  private queue: { seq: number; tx: Transaction }[] = [];
  private presenceTimer: ReturnType<typeof setTimeout> | null = null;
  private presenceBuf: Partial<Presence> = {};
  /** messages that arrived while a big file's document was still downloading (see receive) */
  private waiting: ServerMessage[] | null = null;
  /** messages that arrived before the app was ready for them (see SyncOptions.ready) */
  private held: [ServerMessage, WebSocket][] | null = null;
  private retry = 0;
  private closed = false;
  clientId: string;
  fileId: string | null;
  status: 'connecting' | 'online' | 'offline' = 'connecting';

  constructor(
    fileId: string | null,
    private name: string,
    private handlers: SyncHandlers,
    private opts: SyncOptions = {},
  ) {
    this.fileId = fileId;
    // one id per page (kept across reconnects): not stored per tab, since a duplicated tab copies
    // its storage and two tabs with one id would take each other's place on the server
    this.clientId = opts.clientId ?? Math.random().toString(36).slice(2, 10);
    if (opts.ready) {
      this.held = [];
      void opts.ready.finally(() => {
        const held = this.held ?? [];
        this.held = null;
        for (const [m, ws] of held) this.receive(m, ws);
      });
    }
    this.connect();
  }

  private connect() {
    const base = this.opts.url ?? `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}`;
    // snapshot=1: a big file's document comes over HTTP (one compressed copy shared by everyone joining)
    const params = new URLSearchParams({ name: this.name, client: this.clientId, snapshot: '1' });
    if (this.fileId) params.set('file', this.fileId);
    const WS = this.opts.WebSocket ?? WebSocket;
    const ws = new WS(`${base}/ws?${params}`);
    this.ws = ws;
    this.waiting = null;
    this.setStatus('connecting');
    ws.onmessage = (e) => this.receive(JSON.parse(e.data) as ServerMessage, ws);
    ws.onclose = (e) => {
      if (this.ws !== ws) return;
      this.ws = null;
      this.setStatus('offline');
      if (this.closed) return;
      if (e.code === 4403) {
        this.closed = true;
        this.handlers.onDenied?.(e.reason || 'No access to this file');
        return;
      }
      const delay = Math.min(10000, 500 * 2 ** this.retry++);
      setTimeout(() => this.connect(), delay);
    };
  }

  private setStatus(s: SyncClient['status']) {
    this.status = s;
    this.handlers.onStatus(s);
  }

  close() {
    this.closed = true;
    this.ws?.close();
  }

  /** Messages in order: while the document of a join is downloading, the ones after it wait. */
  private receive(m: ServerMessage, ws: WebSocket) {
    if (this.ws !== ws) return;
    if (this.held) return void this.held.push([m, ws]);
    if (this.waiting) return void this.waiting.push(m);
    if (m.type === 'init' && !m.doc && m.docUrl) {
      this.waiting = [];
      const httpBase = this.opts.url ? this.opts.url.replace(/^ws/, 'http') : '';
      fetch(`${httpBase}${m.docUrl}`, { credentials: 'same-origin' })
        .then((r) => {
          if (!r.ok) throw new Error(String(r.status));
          return r.json() as Promise<DocumentJSON>;
        })
        .then((doc) => {
          if (this.ws !== ws) return;
          const later = this.waiting ?? [];
          this.waiting = null;
          this.onMessage({ ...m, doc });
          for (const x of later) this.receive(x, ws);
        })
        .catch(() => {
          if (this.ws !== ws) return;
          // expired or failed: ask for the whole file over the connection instead
          this.waiting = null;
          this.send({ type: 'resync' });
        });
      return;
    }
    this.onMessage(m);
  }

  private onMessage(m: ServerMessage) {
    switch (m.type) {
      case 'init':
        this.retry = 0;
        this.fileId = m.fileId;
        this.clientId = m.clientId;
        // Local changes made while offline are re-applied on top of the fresh state.
        const unsent = [...this.pending.values(), ...this.queue.map((q) => q.tx)];
        this.pending.clear();
        this.pendingProps.clear();
        this.queue = [];
        this.handlers.onInit(m.doc, m.clientId, m.fileId, m.peers, m.role ?? 'owner');
        this.handlers.onComments(m.comments ?? []);
        this.handlers.onVoting?.(m.voting ?? null);
        this.setStatus('online');
        for (const tx of unsent) {
          this.handlers.onRemote(tx); // re-apply locally on the fresh document
          this.push(tx);
        }
        break;
      case 'ack': {
        const tx = this.pending.get(m.seq);
        this.pending.delete(m.seq);
        if (tx) this.unmark(tx, m.seq);
        break;
      }
      case 'reject':
        console.warn('[sync] server rejected change, resyncing:', m.error);
        this.pending.clear();
        this.pendingProps.clear();
        this.send({ type: 'resync' });
        break;
      case 'tx':
        this.handlers.onRemote(this.filterRemote(m.tx));
        break;
      case 'presence':
        if (m.presence.clientId !== this.clientId) this.handlers.onPresence(m.presence);
        break;
      case 'leave':
        this.handlers.onLeave(m.clientId);
        break;
      case 'focus':
        this.handlers.onFocus(m);
        break;
      case 'voting':
        this.handlers.onVoting?.(m.session);
        break;
      case 'comments':
        this.handlers.onComments(m.comments);
        break;
      case 'rtc':
        this.handlers.onRtc?.(m.from, m.data);
        break;
      case 'ice':
        this.handlers.onIce?.(m.servers);
        break;
      case 'notice':
        this.handlers.onNotice(m.message, m.from);
        break;
    }
  }

  /** Send a locally applied transaction. */
  push(tx: Transaction) {
    const seq = ++this.seq;
    // kept when it's sent again after a reconnect, so the server applies it once
    tx.id ??= `${this.clientId}-${Math.random().toString(36).slice(2, 12)}`;
    this.mark(tx, seq);
    this.pending.set(seq, tx);
    this.send({ type: 'tx', seq, tx: { id: tx.id, ops: tx.ops, label: tx.label } });
  }

  /** Whether edits of ours are still on their way (others' edits may not apply on top of them). */
  get busy() {
    return this.pending.size > 0 || this.queue.length > 0;
  }

  /** Ask the server for the whole file again (when this copy may have drifted from it). */
  resync() {
    this.send({ type: 'resync' });
  }

  private mark(tx: Transaction, seq: number) {
    for (const op of tx.ops) {
      if (op.op !== 'set') continue;
      let m = this.pendingProps.get(op.id);
      if (!m) this.pendingProps.set(op.id, (m = new Map()));
      for (const k of Object.keys(op.props)) m.set(k, seq);
    }
  }

  private unmark(tx: Transaction, seq: number) {
    for (const op of tx.ops) {
      if (op.op !== 'set') continue;
      const m = this.pendingProps.get(op.id);
      if (!m) continue;
      for (const k of Object.keys(op.props)) if (m.get(k) === seq) m.delete(k);
      if (!m.size) this.pendingProps.delete(op.id);
    }
  }

  private filterRemote(tx: Transaction): Transaction {
    if (!this.pendingProps.size) return tx;
    const ops: Op[] = [];
    for (const op of tx.ops) {
      if (op.op === 'set') {
        const m = this.pendingProps.get(op.id);
        if (m) {
          const props = Object.fromEntries(Object.entries(op.props).filter(([k]) => !m.has(k)));
          if (Object.keys(props).length) ops.push({ ...op, props });
          continue;
        }
      }
      ops.push(op);
    }
    return { ...tx, ops };
  }

  vote(op: VoteAction) {
    this.send({ type: 'vote', op });
  }

  rtc(to: string, data: unknown) {
    this.send({ type: 'rtc', to, data });
  }

  comment(op: CommentAction) {
    this.send({ type: 'comment', op });
  }

  presence(p: Partial<Presence>) {
    Object.assign(this.presenceBuf, p);
    if (this.presenceTimer !== null) return;
    this.presenceTimer = setTimeout(() => {
      this.presenceTimer = null;
      const buf = this.presenceBuf;
      this.presenceBuf = {};
      this.send({ type: 'presence', presence: buf });
    }, 40);
  }

  private send(msg: unknown) {
    if (this.ws?.readyState === 1) this.ws.send(JSON.stringify(msg));
    else if ((msg as { type: string }).type === 'tx') {
      const m = msg as { seq: number; tx: Transaction };
      this.pending.delete(m.seq);
      this.queue.push({ seq: m.seq, tx: m.tx });
    }
  }
}

/** Apply a remote transaction op-by-op, skipping ops made invalid by local state. */
/** Apply someone else's transaction, op by op if it doesn't apply whole; returns how many ops were skipped. */
export function applyRemoteLenient(doc: Document, tx: Transaction): number {
  try {
    doc.apply(tx, { remote: true });
    return 0;
  } catch (e) {
    if (!(e instanceof DocumentError)) throw e;
    let skipped = 0;
    doc.batch(() => {
      for (const op of tx.ops) {
        try {
          doc.apply({ ...tx, ops: [op] }, { remote: true });
        } catch {
          skipped++;
        }
      }
    });
    return skipped;
  }
}
